/**
 * 汎用ファイルブラウザ（FileBrowser）用ユーティリティ。
 *
 * トークルームのストレージビュー（fileTableUtils）からトークルーム固有の
 * 陣営（受領/送付）ロジックとメッセージ紐付けを除いて汎用化したもの。
 *
 * - フォルダ階層ツリーの再構築と集計（件数・合計サイズ・最終更新）
 * - 列ソート
 * - zip 一括ダウンロード用のフラット化
 */
import { ZipFileEntry } from "../../utils/downloadFilesAsZip";
import {
  DATE_DISPLAY_TYPE,
  formatDateText,
} from "../date-display/DateDisplay";

// ===== 型定義 =====

export type FileBrowserSortField = "name" | "updated" | "size";
export type FileBrowserSortOrder = "asc" | "desc";

export interface FileBrowserSortState {
  field: FileBrowserSortField;
  order: FileBrowserSortOrder;
}

/** ブラウザに表示する1ファイル（データソース非依存の汎用型） */
export interface FileBrowserFile {
  id: string;
  name: string;
  /** BE のファイル種別（image / video / audio / document / other） */
  fileType: "image" | "video" | "audio" | "document" | "other";
  originalUrl: string;
  optimizedUrl?: string | null;
  waveformUrl?: string | null;
  sizeBytes: number | null;
  /** ISO 日時 */
  updatedAt: string;
  /** 属するフォルダID（null = ルート直下） */
  folderId: string | null;
  /** 有効な共有リンクが存在するか（共有中バッジ用） */
  isShared?: boolean;
  /** オーディオブックマークの件数 */
  bookmarkCount?: number;
  /** ブックマーク作成者（ファイル名列右端のアバター表示用。重複なし） */
  bookmarkUsers?: FileBrowserBookmarkUser[];
  /** このファイルへの編集系操作（リネーム・差し替え・移動・削除）可否 */
  canEdit?: boolean;
}

/** ブックマーク作成者の表示情報 */
export interface FileBrowserBookmarkUser {
  id: number | string;
  name?: string | null;
  avatar?: string | null;
}

/** ブラウザに表示する1フォルダ（空フォルダ含む） */
export interface FileBrowserFolderInfo {
  id: string;
  name: string;
  parentId: string | null;
  /** 空フォルダの更新日表示用フォールバック（ISO 日時） */
  updatedAt?: string | null;
  /** 有効な共有リンクが存在するか（共有中バッジ用） */
  isShared?: boolean;
  /** このフォルダへの編集系操作（作成・リネーム・移動・削除・アップロード）可否 */
  canEdit?: boolean;
}

/**
 * フォルダ直下のファイル集計（BE 提供）。
 * ファイルを遅延ロードする呼び出し側（共有ライブラリ）は、渡した files が
 * 全件とは限らないため、フォルダ行の件数・サイズ・最終更新はこの直下集計
 * + 子フォルダの畳み込みで計算する。
 */
export interface FileBrowserFolderDirectStats {
  fileCount: number;
  totalSize: number | null;
  latestUpdated: string | null;
}

/** フォルダ階層ノード（集計値付き） */
export interface FileBrowserFolderNode {
  id: string;
  name: string;
  parentId: string | null;
  files: FileBrowserFile[];
  children: FileBrowserFolderNode[];
  depth: number;
  /** 配下全ファイル数 */
  totalCount: number;
  /** 配下のサイズ合計（既知サイズのみ。1件も無ければ null） */
  totalSize: number | null;
  /** 配下の最終更新日時 */
  latestUpdated: string | null;
  canEdit: boolean;
  isShared: boolean;
}

// ===== サイズ・日時フォーマット =====

/**
 * file_metadata（JSON文字列）からサイズ(bytes)を取り出す。
 * レガシーバッチは Python dict の repr（シングルクォート）で保存している
 * 場合があるため、JSON.parse に失敗したら正規表現でフォールバックする。
 */
export const parseFileSizeBytes = (metadata?: string | null): number | null => {
  if (!metadata) return null;
  try {
    const parsed = JSON.parse(metadata);
    if (typeof parsed === "object" && parsed !== null) {
      const size = (parsed as Record<string, unknown>).size;
      if (typeof size === "number" && Number.isFinite(size)) return size;
      if (typeof size === "string" && /^\d+$/.test(size)) return Number(size);
    }
  } catch {
    // JSON でない（Python dict repr 等）→ 正規表現で抽出
  }
  const match = metadata.match(/['"]?size['"]?\s*:\s*(\d+)/);
  if (match) return Number(match[1]);
  return null;
};

/** バイト数を人が読める形式に整形する（例: 1.2 MB） */
export const formatFileSize = (bytes: number | null): string => {
  if (bytes === null || !Number.isFinite(bytes)) return "—";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    units.length - 1,
  );
  const value = bytes / Math.pow(k, i);
  return `${parseFloat(value.toFixed(1))} ${units[i]}`;
};

/**
 * ISO 日時 → soremo 共通の日時表記（`M/D H:mm`、他年は年付き）。
 * 独自にゼロ埋め整形すると画面ごとに表記がばらつくため、必ず
 * `formatDateText`（`DateDisplay` と同じ実装）を経由する。
 */
export const formatDateTime = (iso?: string | null): string =>
  formatDateText(iso, DATE_DISPLAY_TYPE.DATE_TIME) || "—";

// ===== ソート =====

const compareByName = (a: FileBrowserFile, b: FileBrowserFile): number =>
  (a.name || "").localeCompare(b.name || "", "ja");

/**
 * 指定列でソートする。
 * 主キーが同値の場合はファイル名の昇順で安定させる。
 * サイズ不明（null）の行は昇順/降順にかかわらず常に末尾に置く。
 */
export const sortFileBrowserFiles = (
  files: FileBrowserFile[],
  sort: FileBrowserSortState,
): FileBrowserFile[] => {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...files].sort((a, b) => {
    switch (sort.field) {
      case "name": {
        return dir * compareByName(a, b);
      }
      case "updated": {
        const diff =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        if (diff !== 0) return dir * diff;
        return compareByName(a, b);
      }
      case "size": {
        if (a.sizeBytes === null && b.sizeBytes === null) {
          return compareByName(a, b);
        }
        if (a.sizeBytes === null) return 1;
        if (b.sizeBytes === null) return -1;
        const diff = a.sizeBytes - b.sizeBytes;
        if (diff !== 0) return dir * diff;
        return compareByName(a, b);
      }
      default:
        return 0;
    }
  });
};

// ===== フォルダ階層ツリー =====

const MAX_FOLDER_DEPTH = 5;

/**
 * ファイルとフォルダ（空フォルダ含む）からツリーを再構築する。
 * 親が見つからないフォルダはルート扱い、最大深さを超えた階層は
 * フラット化する（folderUtils.buildFolderTree と同方針）。
 *
 * folderStats（フォルダ ID → 直下集計）を渡すと、フォルダ行の集計は
 * 「BE の直下集計 + 子フォルダの畳み込み」で計算する（渡された files が
 * 遅延ロードで一部しか無くても、件数・サイズ・最終更新が正しくなる）。
 * 省略時は従来どおり渡された files から集計する。
 */
export const buildFileBrowserTree = (
  files: FileBrowserFile[],
  folders: FileBrowserFolderInfo[],
  folderStats?: ReadonlyMap<string, FileBrowserFolderDirectStats>,
): { rootFolders: FileBrowserFolderNode[]; rootFiles: FileBrowserFile[] } => {
  const rootFiles = files.filter((f) => !f.folderId);
  const foldered = files.filter((f) => f.folderId);

  const nodes = new Map<string, FileBrowserFolderNode>();

  folders.forEach((info) => {
    if (nodes.has(info.id)) return;
    nodes.set(info.id, {
      id: info.id,
      name: info.name,
      parentId: info.parentId,
      files: [],
      children: [],
      depth: 0,
      totalCount: 0,
      totalSize: null,
      latestUpdated: info.updatedAt ?? null,
      canEdit: info.canEdit ?? true,
      isShared: info.isShared ?? false,
    });
  });

  // フォルダ一覧に無い folderId を持つファイルはルート直下として扱う
  foldered.forEach((file) => {
    const node = nodes.get(file.folderId!);
    if (node) {
      node.files.push(file);
    } else {
      rootFiles.push(file);
    }
  });

  const rootFolders: FileBrowserFolderNode[] = [];
  nodes.forEach((node) => {
    if (node.parentId && nodes.has(node.parentId)) {
      nodes.get(node.parentId)!.children.push(node);
    } else {
      rootFolders.push(node);
    }
  });

  // 集計（件数・合計サイズ・最終更新）。
  // 最大深さ超過分のフラット化で子ノードが親へ畳まれる前の、完全な
  // ツリーに対して行う（フラット化は表示用で集計値には影響しない）。
  const aggregate = (node: FileBrowserFolderNode): void => {
    // 空フォルダの場合の最終更新フォールバック（フォルダ自体の更新日時）
    const fallbackUpdated = node.latestUpdated;

    node.children.forEach(aggregate);

    const childSizes = node.children
      .map((c) => c.totalSize)
      .filter((s): s is number => s !== null);
    const childTimestamps = node.children
      .map((c) => (c.latestUpdated ? new Date(c.latestUpdated).getTime() : NaN))
      .filter((t) => !Number.isNaN(t));

    // 直下分: BE 集計があればそれを、無ければ渡された files を使う
    const direct = folderStats?.get(node.id);
    const directCount = folderStats
      ? (direct?.fileCount ?? 0)
      : node.files.length;
    const directSizes = folderStats
      ? direct?.totalSize != null
        ? [direct.totalSize]
        : []
      : node.files
          .map((f) => f.sizeBytes)
          .filter((s): s is number => s !== null);
    const directTimestamps = (
      folderStats
        ? direct?.latestUpdated
          ? [new Date(direct.latestUpdated).getTime()]
          : []
        : node.files.map((f) => new Date(f.updatedAt).getTime())
    ).filter((t) => !Number.isNaN(t));

    node.totalCount =
      directCount + node.children.reduce((sum, c) => sum + c.totalCount, 0);

    const allSizes = [...directSizes, ...childSizes];
    node.totalSize =
      allSizes.length > 0 ? allSizes.reduce((sum, s) => sum + s, 0) : null;

    const timestamps = [...directTimestamps, ...childTimestamps];
    node.latestUpdated =
      timestamps.length > 0
        ? new Date(Math.max(...timestamps)).toISOString()
        : fallbackUpdated;
  };
  rootFolders.forEach(aggregate);

  // 深さ計算 + 最大深さ超過分のフラット化
  const applyDepth = (node: FileBrowserFolderNode, depth: number): void => {
    node.depth = depth;
    if (depth < MAX_FOLDER_DEPTH - 1) {
      node.children.forEach((child) => applyDepth(child, depth + 1));
    } else {
      const flatten = (parent: FileBrowserFolderNode): void => {
        parent.children.forEach((child) => {
          parent.files.push(...child.files);
          flatten(child);
        });
        parent.children = [];
      };
      flatten(node);
    }
  };
  rootFolders.forEach((node) => applyDepth(node, 0));

  return { rootFolders, rootFiles };
};

/**
 * ツリー内の各階層をソートする（破壊的）。
 * - ファイル: 指定の列ソート
 * - フォルダ: 指定列の集計値（最終更新 / 合計サイズ / 名前）でファイルと同じ方向に
 *   ソートする。同値は名前昇順で安定させる。
 */
export const sortFileBrowserTree = (
  rootFolders: FileBrowserFolderNode[],
  sort: FileBrowserSortState,
): void => {
  const dir = sort.order === "asc" ? 1 : -1;
  const compareNodes = (
    a: FileBrowserFolderNode,
    b: FileBrowserFolderNode,
  ): number => {
    const byName = a.name.localeCompare(b.name, "ja");
    switch (sort.field) {
      case "updated": {
        const at = a.latestUpdated ? new Date(a.latestUpdated).getTime() : 0;
        const bt = b.latestUpdated ? new Date(b.latestUpdated).getTime() : 0;
        const diff = at - bt;
        return diff !== 0 ? dir * diff : byName;
      }
      case "size": {
        if (a.totalSize === null && b.totalSize === null) return byName;
        if (a.totalSize === null) return 1;
        if (b.totalSize === null) return -1;
        const diff = a.totalSize - b.totalSize;
        return diff !== 0 ? dir * diff : byName;
      }
      default:
        return dir * byName;
    }
  };
  const sortNodes = (nodes: FileBrowserFolderNode[]): void => {
    nodes.sort(compareNodes);
    nodes.forEach((node) => {
      node.files = sortFileBrowserFiles(node.files, sort);
      sortNodes(node.children);
    });
  };
  sortNodes(rootFolders);
};

/** ノード自身と配下の全フォルダIDを集める（フォルダ移動の循環チェック用） */
export const collectFolderNodeIds = (node: FileBrowserFolderNode): Set<string> => {
  const ids = new Set<string>();
  const walk = (n: FileBrowserFolderNode): void => {
    ids.add(n.id);
    n.children.forEach(walk);
  };
  walk(node);
  return ids;
};

// ===== zip 一括ダウンロード用のフラット化 =====

/**
 * 同一階層内で名前が衝突しないように一意な名前を返す。
 * 既に使われている場合は拡張子を保ったまま連番サフィックス " (n)" を付ける。
 */
const ensureUniqueName = (name: string, usedNames: Set<string>): string => {
  if (!usedNames.has(name)) {
    usedNames.add(name);
    return name;
  }
  const dotIndex = name.lastIndexOf(".");
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const ext = dotIndex > 0 ? name.slice(dotIndex) : "";
  let counter = 1;
  let candidate = `${base} (${counter})${ext}`;
  while (usedNames.has(candidate)) {
    counter += 1;
    candidate = `${base} (${counter})${ext}`;
  }
  usedNames.add(candidate);
  return candidate;
};

/**
 * フォルダノード配下（サブフォルダ含む）の全ファイルをフラットに集める。
 * zip 用と違い階層パスを持たないので、一括文字起こしのように「配下の
 * ファイルそのもの」が要る用途で使う。
 */
export const flattenFolderNodeToFiles = (
  node: FileBrowserFolderNode,
): FileBrowserFile[] => {
  const out: FileBrowserFile[] = [];
  const walk = (n: FileBrowserFolderNode): void => {
    out.push(...n.files);
    n.children.forEach(walk);
  };
  walk(node);
  return out;
};

/**
 * フォルダノード配下の全ファイルを zip 内相対パス付きのフラット配列にする
 * （zip 一括ダウンロード用）。ルートフォルダ自身の名前は zip ファイル名に
 * 使う想定のため、直下のファイルはパスの先頭に付けない。
 */
export const flattenFolderNodeToZipEntries = (
  node: FileBrowserFolderNode,
): ZipFileEntry[] => {
  const entries: ZipFileEntry[] = [];
  const walk = (n: FileBrowserFolderNode, basePath: string): void => {
    const usedNames = new Set<string>();
    n.files.forEach((file) => {
      const uniqueName = ensureUniqueName(file.name, usedNames);
      entries.push({
        path: basePath ? `${basePath}/${uniqueName}` : uniqueName,
        file: { id: file.id, original_url: file.originalUrl, size: file.sizeBytes },
      });
    });
    n.children.forEach((child) => {
      const uniqueDirName = ensureUniqueName(child.name, usedNames);
      walk(child, basePath ? `${basePath}/${uniqueDirName}` : uniqueDirName);
    });
  };
  walk(node, "");
  return entries;
};

/**
 * フラットなファイル / フォルダ一覧から、rootFolderId 起点の zip エントリを
 * 組み立てる。ファイルを遅延ロードする呼び出し側が「配下全件取得 API」の
 * 結果（フラット配列）から zip を作るときに使う
 * （flattenFolderNodeToZipEntries と同じ階層パス・名前衝突回避）。
 */
export const buildZipEntriesForFolder = (
  files: FileBrowserFile[],
  folders: FileBrowserFolderInfo[],
  rootFolderId: string,
): ZipFileEntry[] => {
  const { rootFolders } = buildFileBrowserTree(files, folders);
  const findNode = (
    nodes: FileBrowserFolderNode[],
  ): FileBrowserFolderNode | null => {
    for (const node of nodes) {
      if (node.id === rootFolderId) return node;
      const found = findNode(node.children);
      if (found) return found;
    }
    return null;
  };
  const node = findNode(rootFolders);
  return node ? flattenFolderNodeToZipEntries(node) : [];
};
